import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useEffect} from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {
  ConfiguratorResultType,
  useProcessFlowchartEditorConfigurator,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import HorizontalThrobber, {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ProcessFlowchartConfigEditorQuery} from './__generated/ProcessFlowchartConfigEditorQuery';
import {ProcessFlowchartConfigEditorQueryVariables} from './__generated/ProcessFlowchartConfigEditorQuery';
import {ProcessFlowchartConfigWithValidSchemaGQL} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorController from './ProcessFlowchartConfigEditorController';

// We disable the graphql/template-strings rule here because the flowchartConfigVersionId variable is used in
// the ProcessFlowchartConfigEditorGraphQL fragment, but eslint can't detect that usage.
/* eslint-disable graphql/template-strings */
export const PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY: DocumentNode = gql`
  query ProcessFlowchartConfigEditorQuery(
    $flowchartConfigId: StrictString!
    $flowchartConfigVersionId: StrictString
  ) @skipAudit @engComponent(name: WORKFLOWS) {
    processFlowchartConfig(id: $flowchartConfigId) {
      ... on ProcessFlowchartConfigNode {
        ...ProcessFlowchartConfigEditorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartConfigEditor').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
/* eslint-enable graphql/template-strings */

function NonConfigurableSchema(): JSX.Element {
  return <div>This flowchart is not user-configurable.</div>;
}

function NoFlowchartConfigVersion(): JSX.Element {
  return <div>No flowchart config version found</div>;
}

interface ProcessFlowchartConfigEditorProps {
  flowchartTypeName: string;
  flowchartConfigId: string;
  flowchartConfigVersionId?: string;
  locked: boolean;
}

/**
 * Process flowchart config editor that queries the flowchart config and displays the
 * flowchart editor content.
 *
 * Renders the current flowchart config version unless a specific version is specified.
 */
export default function ProcessFlowchartConfigEditor(props: ProcessFlowchartConfigEditorProps): JSX.Element {
  const configuratorResult = useProcessFlowchartEditorConfigurator(props.flowchartTypeName);

  // Handle error notification in useEffect to prevent multiple notifications on re-renders
  useEffect(() => {
    if (configuratorResult.resultType === ConfiguratorResultType.ERROR) {
      notifications.error(`Unable to load flowchart type configuration for ${props.flowchartTypeName}.`);
    }
  }, [configuratorResult.resultType, props.flowchartTypeName]);

  if (configuratorResult.resultType === ConfiguratorResultType.PENDING) {
    return <HorizontalThrobber />;
  }

  if (configuratorResult.resultType === ConfiguratorResultType.ERROR) {
    return <></>;
  }

  const flowchartEditorConfigurator = configuratorResult.configurator;

  return (
    <BenchlingQuery<ProcessFlowchartConfigEditorQuery, ProcessFlowchartConfigEditorQueryVariables>
      query={PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        flowchartConfigId: props.flowchartConfigId,
        flowchartConfigVersionId: props.flowchartConfigVersionId ?? null,
      }}
      fetchPolicy="cache-and-network"
    >
      {(data, {refetch}) => {
        if (isInaccessible(data.processFlowchartConfig)) {
          return (
            <WorkspaceItemInaccessibleGuard
              inaccessibleInfo={data.processFlowchartConfig}
              modelType="processFlowchartConfig"
            />
          );
        }
        const flowchartConfigNode = data.processFlowchartConfig;

        if (isInaccessible(flowchartConfigNode.schema)) {
          return <NonConfigurableSchema />;
        }

        const currentFlowchartConfigVersion = flowchartConfigNode.flowchartConfigVersion;
        if (currentFlowchartConfigVersion == null) {
          return <NoFlowchartConfigVersion />;
        }

        return (
          <ProcessFlowchartConfigEditorController
            flowchartEditorConfigurator={flowchartEditorConfigurator}
            // flowchartConfigNode.schema guaranteed by checks above
            flowchartConfig={flowchartConfigNode as ProcessFlowchartConfigWithValidSchemaGQL}
            flowchartConfigVersion={currentFlowchartConfigVersion}
            locked={props.locked}
            refetchFlowchartConfig={() => {
              refetch();
            }}
          />
        );
      }}
    </BenchlingQuery>
  );
}
